import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { phoneNumberNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { App as AntApp, Checkbox, Col, Divider, Form, Input, Modal, Row, Tree } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Key, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAdminCreateUserWithAgentUserAccounts,
  useEnumeration,
  useGetPermissionDefinitions,
} from "../../../../common/api/queries";
import { MOBILE_NUMBER_COUNTRY_WHITELIST, ModalSizes, rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { AntTreeStrictCheckedProps } from "../../../../common/types";
import { useIsSystemAdmin } from "../../../auth/hooks";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { EnumerationType } from "../../../enumerations/enums";
import { DefaultPermissionsFormPart } from "../../../user/components/forms/DefaultPermissionsFormPart";
import { AdminCreateUserWithAgentUserAccounts } from "../../../user/types";
import {
  buildPermissionTreeNodes,
  filterPermissionsWithPrerequisites,
  isSystemAdminOnlyPermission,
} from "../../../user/utils";
import { AgentType } from "../../enums";
import { Agent } from "../../types";
import { isLegalAgent } from "../../utils";

interface Props {
  open: boolean;
  agent: Agent;
  onFormCancel: () => void;
}

export const AdminCreateUserForAgentForm = ({ open, agent, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();
  const topAgentAllowedFeatures = useEnumeration(EnumerationType.TOP_AGENT_ALLOWED_FEATURES);

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const [form] = Form.useForm<AdminCreateUserWithAgentUserAccounts>();

  const adminCreateUserWithAgentUserAccounts = useAdminCreateUserWithAgentUserAccounts({ globalSpinner: false });

  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);

  const handleFormCancel = () => {
    setSelectedPermissions([]);
    onFormCancel();
  };

  const handlePermissionTreeCheck = (checked: Key[] | AntTreeStrictCheckedProps) => {
    const checkedPermissions = (checked as AntTreeStrictCheckedProps)?.checked.map(
      (checked) => Permission[checked as keyof typeof Permission],
    );

    setSelectedPermissions(filterPermissionsWithPrerequisites(permissionDefinitions, checkedPermissions));
  };

  const handleSystemAdminChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      setSelectedPermissions(
        selectedPermissions.filter((permission) => !isSystemAdminOnlyPermission(permissionDefinitions, permission)),
      );
    }
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (selectedPermissions.length === 0) {
          message.error(t("user.helpers.noPermissions"));
        } else {
          adminCreateUserWithAgentUserAccounts.mutate(
            {
              ...values,
              accounts: values.accounts.map((acc) => ({ ...acc, agentId: agent.id, permissions: selectedPermissions })),
            },
            {
              onSuccess: handleFormCancel,
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  const colSpan = 8;

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t("user.titles.createUser")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={adminCreateUserWithAgentUserAccounts.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={handleFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="adminCreateUserForAgentForm" onFinish={handleFormSubmit}>
        <Divider titlePlacement="start" style={{ marginTop: 0 }}>
          {t("user.titles.basicData")}
        </Divider>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="name"
              label={t("user.attrs.name")}
              rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.nameRegex)]}
            >
              <Input placeholder={t("user.helpers.userNamePlaceholder")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="email"
              label={t("user.attrs.email")}
              rules={[validations.notBlank, validations.size(1, 254), validations.email]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="phone"
              label={t("user.attrs.phone")}
              rules={[validations.notBlank, validations.mobilePhoneNumber(MOBILE_NUMBER_COUNTRY_WHITELIST)]}
              normalize={phoneNumberNormalizeFunction}
            >
              <Input />
            </Form.Item>
          </Col>

          {isCurrentUserSystemAdmin && (
            <Col span={colSpan}>
              <Form.Item
                name="systemAdmin"
                className="form-item-without-label"
                valuePropName="checked"
                initialValue={false}
                rules={[validations.none]}
              >
                <Checkbox onChange={handleSystemAdminChange}>{t("user.attrs.systemAdmin")}</Checkbox>
              </Form.Item>
            </Col>
          )}
        </Row>

        <Divider titlePlacement="start">{t("user.titles.account")}</Divider>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["accounts", 0, "representingAgentId"]}
              label={
                <LabelWithTooltip
                  label={t("user.attrs.representingAgentId")}
                  tooltip={t("user.helpers.representingAgentInfo")}
                />
              }
              rules={[agent.type === AgentType.LEGAL ? validations.notNull : validations.none]}
            >
              <AgentSelect
                optionsProps={{
                  allowedIds: isLegalAgent(agent)
                    ? agent.representatives.map((representative) => representative.id)
                    : undefined,
                  forceRender: true,
                }}
                disabled={agent.type !== AgentType.LEGAL}
                allowClear
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["accounts", 0, "disabled"]}
              className="form-item-without-label"
              valuePropName="checked"
              initialValue={false}
              rules={[validations.none]}
            >
              <Checkbox>{t("user.attrs.disabled")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" className="divider-subheader">
          {t("user.titles.permissionsSettings")}
        </Divider>

        <DefaultPermissionsFormPart
          allowedFeatures={topAgentAllowedFeatures}
          onPermissionsSet={setSelectedPermissions}
        />

        <Row gutter={rowGutter}>
          <Col span={colSpan * 2}>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.systemAdmin !== next.systemAdmin}>
              {({ getFieldValue }) => (
                <Form.Item label={t("user.attrs.permissionsLabel")} required>
                  <Tree
                    showLine={{ showLeafIcon: false }}
                    checkStrictly
                    checkable
                    checkedKeys={{ checked: selectedPermissions, halfChecked: [] }}
                    treeData={buildPermissionTreeNodes(
                      permissionDefinitions,
                      topAgentAllowedFeatures,
                      selectedPermissions,
                      isCurrentUserSystemAdmin,
                      getFieldValue("systemAdmin"),
                    )}
                    onCheck={handlePermissionTreeCheck}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
